import { AccordionItemContentElement } from '@typedream/components';
import {
  AccordionItemContentElement as AccordionItemContentElementType,
  DreamValue,
} from '@typedream/core';
import { findNodePath, PlateRenderElementProps } from '@udecode/plate-core';
import { pick } from 'lodash';
import { useDrop } from 'react-dnd';
import { isBlockEmpty } from '../../../editorTools/queries';
import { DreamEditor } from '../../../types';
import { DragItem } from '../../../plugins/dnd/types';
import { insertItemIntoAccordionItemContent } from '../transforms/insertItemIntoAccordionItemContent';

function DropTarget(
  props: PlateRenderElementProps<DreamValue, AccordionItemContentElementType>,
) {
  const { element, children, editor } = props;

  const [{ isOver, canDrop }, dropTarget] = useDrop({
    accept: 'block',
    canDrop: () => true,
    collect: (monitor) => ({
      isOver: !!monitor.isOver({ shallow: true }),
      canDrop: !!monitor.canDrop(),
    }),
    drop: (item: DragItem) => {
      const path = findNodePath(editor, element);
      if (path) {
        insertItemIntoAccordionItemContent(editor as unknown as DreamEditor, path, item);
      }
    },
  });

  return (
    <div
      ref={dropTarget}
      style={{
        margin: '1px 0 2px',
        border: isOver
          ? '3px solid var(--dropline)'
          : '1px dashed var(--accent)',
        background: isOver && canDrop ? 'var(--dropBackground)' : undefined,
        borderRadius: 10,
      }}
      contentEditable={false}
    >
      <div
        style={{
          color: 'var(--text-lighter)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          height: 100,
        }}
      >
        <span style={{ color: 'var(--accent)' }}>Drop items inside</span>
      </div>

      <span style={{ display: 'none' }}>{children}</span>
    </div>
  );
}

export function AccordionItemContentElementEditor(
  props: PlateRenderElementProps<DreamValue, AccordionItemContentElementType>,
) {
  const { editor, element, children } = props;

  return (
    <AccordionItemContentElement
      {...pick(props, ['element', 'className', 'attributes'])}
    >
      {isBlockEmpty(editor as unknown as DreamEditor, element) ? (
        <DropTarget {...props} />
      ) : (
        children
      )}
    </AccordionItemContentElement>
  );
}
